import React, { useEffect, useRef, useState } from "react";
import "ol/ol.css";
import "ol-layerswitcher/dist/ol-layerswitcher.css";
import "../App.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { TileWMS, XYZ } from "ol/source";
import { fromLonLat } from "ol/proj";
import LayerSwitcher from "ol-layerswitcher";
import { ScaleLine, FullScreen, defaults as defaultControls, Control } from "ol/control";
import GeoJSON from "ol/format/GeoJSON";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Style from "ol/style/Style";
import { RegularShape, Fill, Stroke } from "ol/style";
import proj4 from "proj4";
import { Chart, BarController, BarElement, LinearScale, CategoryScale } from "chart.js";
import { Line } from "@ant-design/plots";
import api from "../services/api";
import { Button } from "antd";

// Gradient color function
const getColor = (value, min, max) => {
  const normalizedValue = (value - min) / (max - min);
  const hue = normalizedValue * 240;
  return `hsl(${hue}, 100%, 50%)`;
};

// Custom Legend Control
class LegendControl extends Control {
  constructor(opt_options) {
    const options = opt_options || {};
    const element = document.createElement("div");
    element.className = "legend ol-unselectable ol-control";
    element.style.cssText = "background-color: rgba(0, 0, 0, 0.8); padding: 10px; border-radius: 5px; color: white;";
    super({ element: element, target: options.target });

    this.minValue = 0;
    this.maxValue = 0;
  }

  setMap(map) {
    super.setMap(map);
    if (map) this.render();
  }

  updateLegend(minValue, maxValue) {
    this.minValue = minValue;
    this.maxValue = maxValue;
    this.render();
  }

  render() {
    const midValue = (this.minValue + this.maxValue) / 2;
    const gradient = `linear-gradient(to bottom, 
      ${getColor(this.minValue, this.minValue, this.maxValue)} 0%, 
      ${getColor(midValue, this.minValue, this.maxValue)} 50%, 
      ${getColor(this.maxValue, this.minValue, this.maxValue)} 100%)`;

    this.element.innerHTML = `
      <div style="font-size: 12px; font-weight: bold; margin-bottom: 5px;">Water Equivalent <br/>Thickness (cm)</div>
      <div style="display: flex; align-items: center;">
        <div style="width: 20px; height: 150px; background: ${gradient}; margin-right: 10px;"></div>
        <div style="display: flex; flex-direction: column; justify-content: space-between; height: 150px;">
          <span>${this.minValue.toFixed(2)}</span>
          <span>${midValue.toFixed(2)}</span>
          <span>${this.maxValue.toFixed(2)}</span>
        </div>
      </div>`;
  }
}

function Index() {
  Chart.register(BarController, BarElement, LinearScale, CategoryScale);
  const mapRef = useRef(null);
  const [popupContent, setPopupContent] = useState("");
  const [popupStyle, setPopupStyle] = useState({ left: "0px", top: "0px" });
  const [graphData, setGraphData] = useState([]);
  const [loading, setLoading] = useState(true);

  const sourceProj = "EPSG:3857";
  const destProj = "EPSG:4326";

  // // Fetch graph data separately
  // useEffect(() => {
  //   api.get("/graph/")
  //     .then((res) => setGraphData(res.data.data))
  //     .catch((err) => console.error("Graph error:", err));
  // }, []);

  // Initialize map and fetch GeoJSON
  useEffect(() => {
    if (!mapRef.current) return;
    setLoading(true);

    const map = new Map({
      controls: defaultControls().extend([new FullScreen(), new LegendControl()]),
      target: mapRef.current,
      layers: [
        new TileLayer({ source: new OSM(), title: "OSM", visible: false }),
        new TileLayer({ source: new XYZ({ url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" }), title: "Satellite", visible: true }),
      ],
      view: new View({
        center: fromLonLat([38.97, 8.74]),
        zoom: 6.5,
        minZoom: 5.5,
        maxZoom: 8,
      }),
    });

    const geoServerLayer = new TileLayer({
      source: new TileWMS({
        url: "http://196.188.116.41:8082/geoserver/geoserver/wms",
        params: { LAYERS: "geoserver:eth_aoi", TILED: true },
        serverType: "geoserver",
        transition: 0,
      }),
      visible: true,
      opacity: 1,
      zIndex: 2,
    });
    map.addLayer(geoServerLayer);

    map.addControl(new LayerSwitcher({ reverse: true, activationMode: "click", groupSelectStyle: "children" }));
    map.addControl(new ScaleLine({ units: "metric" }));

    // Fetch geojson data
    api.get("/geojson/", { params: { start: 2024, end: 2024 } })
      .then((res) => {
        const geojsonFormat = new GeoJSON();
        const features = geojsonFormat.readFeatures(res.data, { featureProjection: map.getView().getProjection() });

        const lweValues = features.map(f => f.get("LWE_Thickness"));
        const minLWE = Math.min(...lweValues);
        const maxLWE = Math.max(...lweValues);

        const legendControl = map.getControls().getArray().find(control => control instanceof LegendControl);
        if (legendControl) legendControl.updateLegend(minLWE, maxLWE);

        const vectorSource = new VectorSource({ features });
        const geojsonLayer = new VectorLayer({
          source: vectorSource,
          style: feature => new Style({
            image: new RegularShape({
              points: 4,
              radius: 28,
              fill: new Fill({ color: getColor(feature.get("LWE_Thickness"), minLWE, maxLWE) }),
              stroke: new Stroke({ color: getColor(feature.get("LWE_Thickness"), minLWE, maxLWE) }),
            }),
          }),
          zIndex: 1,
          title: "LWE",
        });

        map.addLayer(geojsonLayer);
        setLoading(false);

        // Popup logic
        map.on("singleclick", (evt) => {
          const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f);
          if (feature) {
            const flatCoordinates = feature.getGeometry().getCoordinates();
            const [lon, lat] = proj4(sourceProj, destProj, flatCoordinates);
            const val = feature.get("LWE_Thickness");
            const content = `<u><b>Ethiopia Water Equivalent Thickness (2024)</b></u><br/>
              Latitude: <span style="background-color:#848484;color:white">${lat.toFixed(4)}</span><br/>
              Longitude: <span style="background-color:#848484;color:white">${lon.toFixed(4)}</span><br/>
              Water Equivalent Thickness: <span style="background-color:${getColor(val, minLWE, maxLWE)};color:white">${val.toFixed(3)}</span>`;

            setPopupContent(content);
            const pixel = map.getPixelFromCoordinate(evt.coordinate);
            setPopupStyle({ left: `${pixel[0]}px`, top: `${pixel[1]}px` });
            document.getElementById("popup").classList.add("active");
          }
        });

        map.on("click", () => {
          document.getElementById("popup").classList.remove("active");
        });
      })
      .catch((err) => {
        console.error("GeoJSON error:", err);
        setLoading(false);
      });

    return () => map.setTarget(null); // Cleanup
  }, []);

  const chartConfig = {
    data: graphData,
    xField: "year",
    yField: "lwe_thickness",
    xAxis: { title: { text: "Year" } },
    yAxis: { title: { text: "LWE Thickness (cm)" } },
    smooth: true,
    point: { shape: "circle" },
  };

  const closePopup = () => {
    document.getElementById("popup").classList.remove("active");
    return false;
  };

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading data...</p>
        </div>
      )}
      <div className="main-container">
        <div id="map" className="map" ref={mapRef} style={{ width: "100%", height: "100vh" }}></div>
        <div id="popup" className="ol-popup-home" style={popupStyle}>
          <a href="#" id="popup-closer" className="ol-popup-home-closer" onClick={closePopup}>
            <Button>Close</Button>
          </a>
          <div id="popup-content" dangerouslySetInnerHTML={{ __html: popupContent }} />
        </div>
      </div>
      {/* <h3>Ethiopia Water Equivalent Thickness (2002–2024)</h3>
      <div style={{ backgroundColor: "white", padding: "10px", borderRadius: "5px", height: "30vh" }}>
        <Line {...chartConfig} /> 
      </div> */}
    </>
  );
}

export default Index;